import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useEffectivePermissions } from '../hooks/useEffectivePermissions';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import {
  Shield, Save, Lock, ChevronDown, ChevronRight, FilterX, AlertTriangle,
  Plus, Pencil, Trash2, Sparkles, ShieldAlert,
} from 'lucide-react';
import api from '../lib/api';

// ─── Chip styling ────────────────────────────────────────────────────────
const CHIP_LEVELS = {
  none: { label: 'No Access', cls: 'bg-slate-100 text-slate-600 border-slate-200' },
  view: { label: 'View', cls: 'bg-blue-100 text-blue-700 border-blue-200' },
  full: { label: 'Full', cls: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  custom: { label: 'Custom', cls: 'bg-amber-100 text-amber-800 border-amber-300' },
};

const ALL_CAPS = ['view', 'add', 'edit', 'delete', 'export', 'approve'];
const CAP_LABEL = {
  view: 'View', add: 'Add', edit: 'Edit', delete: 'Delete', export: 'Export', approve: 'Approve',
};

const HR_GRANT_META = {
  hr_core: { label: 'HR-Core', desc: 'Employee Core records (identity, contact, assignment)' },
  payroll: { label: 'Payroll', desc: 'Banking, compensation, withholdings' },
  executive_compensation: { label: 'Executive Compensation', desc: 'Walled-off executive pay records; highest sensitivity', highSensitivity: true },
  confidential: { label: 'Confidential', desc: 'SSN, EEO, I-9, WOTC, demographics' },
};

// ─── Helpers ─────────────────────────────────────────────────────────────
function resolveLevel(caps) {
  if (!caps || caps.length === 0) return 'none';
  if (caps.length === ALL_CAPS.length) return 'full';
  if (caps.length === 1 && caps[0] === 'view') return 'view';
  return 'custom';
}

function setsEqual(a, b) {
  const sa = new Set(a || []); const sb = new Set(b || []);
  if (sa.size !== sb.size) return false;
  for (const x of sa) if (!sb.has(x)) return false;
  return true;
}

function isModuleOverridden(role, moduleKey) {
  if (!role) return false;
  // "Overridden" = deviates from system defaults (survives reload).
  const def = role.default_capabilities?.[moduleKey] || [];
  const cur = role.capabilities?.[moduleKey] || [];
  return !setsEqual(def, cur);
}

function isModuleDirty(role, moduleKey) {
  if (!role) return false;
  // "Dirty" = deviates from last-loaded snapshot (unsaved changes in this session).
  const orig = role.original_capabilities?.[moduleKey] || [];
  const cur = role.capabilities?.[moduleKey] || [];
  return !setsEqual(orig, cur);
}

function categorize(modules) {
  const buckets = {};
  modules.forEach(m => {
    buckets[m.category] = buckets[m.category] || [];
    buckets[m.category].push(m);
  });
  return Object.entries(buckets); // [category, modules[]]
}

// ─── Component ───────────────────────────────────────────────────────────
export default function Permissions() {
  const { user } = useAuth();
  const navigate = useNavigate();
  // Phase 3.12 — agree with sidebar. Sidebar uses engineModule:'admin' so any
  // user with admin:view (or any admin cap) sees the link. The route guard
  // here used to bounce non-admin role-string users → "visible then bounced".
  // Now both sources read from useEffectivePermissions. The page renders for
  // anyone with admin:view; write controls (save / new-role / delete-role)
  // already use RequireCap, so a view-cap user gets a read-only page.
  const enginePerms = useEffectivePermissions();
  const canViewAdmin = enginePerms.can('admin', 'view');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snapshot, setSnapshot] = useState(null); // { modules, capabilities, hr_grants, scopes, roles }
  const [activeRoleKey, setActiveRoleKey] = useState(null);
  const [showOnlyOverrides, setShowOnlyOverrides] = useState(false);
  const [openCategories, setOpenCategories] = useState({});
  const [confirmExecComp, setConfirmExecComp] = useState({ open: false, role: null, nextState: false });
  // Phase 3.5 (2026-06-23) — Custom roles.
  const [newRoleOpen, setNewRoleOpen] = useState(false);
  const [editRoleOpen, setEditRoleOpen] = useState(false);
  const [deleteRoleOpen, setDeleteRoleOpen] = useState(false);

  useEffect(() => {
    // Wait for engine perms to resolve before deciding to bounce — avoids
    // racing on cold load.
    if (user && enginePerms.loaded && !canViewAdmin && user.role !== 'admin') {
      toast.error('Access denied. admin:view capability required.');
      navigate('/dashboard');
    }
  }, [user, enginePerms.loaded, canViewAdmin, navigate]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/permissions/engine');
      const data = res.data;
      // Snapshot original_capabilities for "Show only overrides" diffing.
      data.roles.forEach(r => {
        r.original_capabilities = JSON.parse(JSON.stringify(r.capabilities));
        r.original_hr_grants = [...(r.hr_grants || [])];
      });
      setSnapshot(data);
      const firstNonAdmin = data.roles.find(r => !r.locked);
      setActiveRoleKey((prev) => prev || firstNonAdmin?.key || data.roles[0]?.key);
      // Start with all categories open
      const cats = {};
      data.modules.forEach(m => { cats[m.category] = true; });
      setOpenCategories(cats);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load permissions');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const activeRole = snapshot?.roles.find(r => r.key === activeRoleKey) || null;
  const hasUnsavedChanges = useMemo(() => {
    if (!snapshot) return false;
    return snapshot.roles.some(r => {
      if (r.locked) return false;
      for (const m of snapshot.modules) {
        const orig = r.original_capabilities?.[m.key] || [];
        const cur = r.capabilities?.[m.key] || [];
        if (!setsEqual(orig, cur)) return true;
      }
      if (!setsEqual(r.original_hr_grants || [], r.hr_grants || [])) return true;
      return false;
    });
  }, [snapshot]);

  // Warn on navigation with unsaved changes
  useEffect(() => {
    const handler = (e) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [hasUnsavedChanges]);

  // ─── Mutators ──
  const setRoleCaps = (roleKey, moduleKey, caps) => {
    setSnapshot(prev => {
      const next = { ...prev, roles: prev.roles.map(r => {
        if (r.key !== roleKey) return r;
        // Belt-and-suspenders: supervisor never gets approve
        const safeCaps = roleKey === 'supervisor' ? caps.filter(c => c !== 'approve') : caps;
        return { ...r, capabilities: { ...r.capabilities, [moduleKey]: safeCaps } };
      })};
      return next;
    });
  };

  const applyChip = (roleKey, moduleKey, chip) => {
    const caps = chip === 'none' ? [] : chip === 'view' ? ['view'] : [...ALL_CAPS];
    setRoleCaps(roleKey, moduleKey, caps);
  };

  const toggleCap = (roleKey, moduleKey, cap, value) => {
    const role = snapshot.roles.find(r => r.key === roleKey);
    const cur = new Set(role.capabilities[moduleKey] || []);
    if (value) cur.add(cap); else cur.delete(cap);
    setRoleCaps(roleKey, moduleKey, Array.from(cur));
  };

  const toggleHRGrant = (roleKey, grant, value) => {
    // Executive comp needs explicit confirmation when being granted.
    if (grant === 'executive_compensation' && value) {
      setConfirmExecComp({ open: true, role: roleKey, nextState: true });
      return;
    }
    setSnapshot(prev => ({
      ...prev,
      roles: prev.roles.map(r => {
        if (r.key !== roleKey) return r;
        const next = new Set(r.hr_grants || []);
        if (value) next.add(grant); else next.delete(grant);
        return { ...r, hr_grants: Array.from(next) };
      }),
    }));
  };

  const confirmAndApplyExecComp = () => {
    const { role, nextState } = confirmExecComp;
    setSnapshot(prev => ({
      ...prev,
      roles: prev.roles.map(r => {
        if (r.key !== role) return r;
        const next = new Set(r.hr_grants || []);
        if (nextState) next.add('executive_compensation'); else next.delete('executive_compensation');
        return { ...r, hr_grants: Array.from(next) };
      }),
    }));
    setConfirmExecComp({ open: false, role: null, nextState: false });
  };

  const saveAll = async () => {
    if (!snapshot) return;
    setSaving(true);
    try {
      const dirty = snapshot.roles.filter(r => {
        if (r.locked) return false;
        for (const m of snapshot.modules) {
          if (!setsEqual(r.original_capabilities?.[m.key] || [], r.capabilities?.[m.key] || [])) return true;
        }
        return !setsEqual(r.original_hr_grants || [], r.hr_grants || []);
      });
      for (const r of dirty) {
        // Send the full set of modules that deviate from system defaults so the
        // backend has the complete override set (not just the diff vs last load).
        const overrides = {};
        for (const m of snapshot.modules) {
          const def = r.default_capabilities?.[m.key] || [];
          const cur = r.capabilities?.[m.key] || [];
          if (!setsEqual(def, cur)) overrides[m.key] = cur;
        }
        const grantsChanged = !setsEqual(r.original_hr_grants || [], r.hr_grants || []);
        await api.put('/permissions/engine', {
          role: r.key,
          capabilities: overrides,
          hr_grants: grantsChanged ? r.hr_grants : undefined,
        });
      }
      toast.success(`Saved permissions for ${dirty.length} role(s)`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const discardChanges = () => {
    if (!hasUnsavedChanges) return;
    if (window.confirm('Discard all unsaved changes?')) load();
  };

  if (loading || !snapshot) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="permissions-loading">
        <div className="text-slate-500">Loading permissions…</div>
      </div>
    );
  }

  const categories = categorize(snapshot.modules);

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="permissions-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-blue-50 border border-blue-200">
            <Shield className="w-5 h-5 text-blue-700" />
          </div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">Permissions</h1>
            <p className="text-sm text-slate-600">Role-level capability and HR-grant configuration</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {hasUnsavedChanges && (
            <Badge className="bg-amber-100 text-amber-800 border-amber-300" data-testid="unsaved-badge">
              Unsaved changes
            </Badge>
          )}
          <Button variant="outline" onClick={discardChanges} disabled={!hasUnsavedChanges || saving}
                  data-testid="discard-btn" className="min-h-[44px]">
            Discard
          </Button>
          <Button onClick={saveAll} disabled={!hasUnsavedChanges || saving}
                  data-testid="save-permissions-btn" className="min-h-[44px]">
            <Save className="w-4 h-4 mr-2" /> {saving ? 'Saving…' : 'Save changes'}
          </Button>
        </div>
      </div>

      <Tabs defaultValue="editor" className="w-full">
        <TabsList data-testid="permissions-tabs">
          <TabsTrigger value="editor" data-testid="tab-role-editor">Role Editor</TabsTrigger>
          <TabsTrigger value="matrix" data-testid="tab-matrix">Matrix Overview</TabsTrigger>
        </TabsList>

        {/* ─── Role Editor ───────────────────────── */}
        <TabsContent value="editor">
          <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-4">
            {/* Left rail — roles */}
            <Card className="lg:sticky lg:top-4 lg:self-start">
              <CardHeader className="pb-3 flex flex-row items-center justify-between space-y-0">
                <CardTitle className="text-base">Roles</CardTitle>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-8 px-2 text-xs"
                  data-testid="new-role-btn"
                  onClick={() => setNewRoleOpen(true)}
                >
                  <Plus className="w-3.5 h-3.5 mr-1" /> New
                </Button>
              </CardHeader>
              <CardContent className="space-y-1.5 max-h-[60vh] overflow-y-auto">
                {snapshot.roles.map(r => {
                  const active = r.key === activeRoleKey;
                  const hasOverrides = !r.locked && (() => {
                    for (const m of snapshot.modules) {
                      if (!setsEqual(r.default_capabilities?.[m.key] || [], r.capabilities?.[m.key] || [])) return true;
                    }
                    return !setsEqual(r.default_hr_grants || [], r.hr_grants || []);
                  })();
                  return (
                    <button
                      key={r.key}
                      type="button"
                      onClick={() => setActiveRoleKey(r.key)}
                      data-testid={`role-card-${r.key}`}
                      className={`w-full text-left px-3 py-2.5 rounded-lg border transition-colors min-h-[44px] ${
                        active ? 'bg-blue-600 text-blue-50 border-blue-700 shadow-sm' : 'bg-surface text-slate-800 border-slate-200 hover:bg-slate-50'
                      }`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-medium text-sm flex items-center gap-1.5">
                          {r.is_custom && <Sparkles className={`w-3.5 h-3.5 ${active ? 'text-amber-200' : 'text-amber-500'}`} />}
                          {r.label}
                        </span>
                        {r.locked && <Lock className={`w-3.5 h-3.5 ${active ? 'text-white/80' : 'text-slate-400'}`} />}
                      </div>
                      <div className="flex items-center gap-1 mt-0.5">
                        <span className={`text-xs ${active ? 'text-blue-100' : 'text-slate-500'}`}>scope: {r.default_scope}</span>
                        {hasOverrides && (
                          <span className={`text-xs ml-1 px-1.5 py-0 rounded ${active ? 'bg-amber-300 text-amber-900' : 'bg-amber-100 text-amber-800'}`}>
                            modified
                          </span>
                        )}
                        {r.is_custom && (
                          <span className={`text-xs ml-1 px-1.5 py-0 rounded ${active ? 'bg-blue-300 text-blue-900' : 'bg-blue-50 text-blue-700 border border-blue-200'}`}>
                            custom
                          </span>
                        )}
                      </div>
                    </button>
                  );
                })}
              </CardContent>
            </Card>

            {/* Detail pane */}
            {activeRole && (
              <div className="space-y-4">
                {activeRole.locked && (
                  <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 flex items-start gap-3">
                    <Lock className="w-5 h-5 text-slate-500 shrink-0 mt-0.5" />
                    <div>
                      <div className="font-semibold text-slate-900">System Administrator is locked</div>
                      <div className="text-sm text-slate-600">Full access across all modules and HR sub-areas. Cannot be edited.</div>
                    </div>
                  </div>
                )}

                {/* Custom-role banner with Edit / Delete actions */}
                {activeRole.is_custom && !activeRole.locked && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3" data-testid="custom-role-banner">
                    <Sparkles className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-slate-900">{activeRole.label}</div>
                      <div className="text-xs text-slate-600">
                        Admin-defined role · default scope <strong>{activeRole.default_scope}</strong>
                        {activeRole.base_role ? <> · cloned from <code className="bg-surface px-1 rounded">{activeRole.base_role}</code></> : ' · started blank'}
                      </div>
                      {activeRole.description && (
                        <div className="text-xs text-slate-500 mt-1">{activeRole.description}</div>
                      )}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <Button size="sm" variant="outline" onClick={() => setEditRoleOpen(true)} data-testid="edit-role-btn">
                        <Pencil className="w-3.5 h-3.5 mr-1" /> Edit
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => setDeleteRoleOpen(true)}
                              className="text-rose-700 border-rose-200 hover:bg-rose-50" data-testid="delete-role-btn">
                        <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete
                      </Button>
                    </div>
                  </div>
                )}

                {/* An override granting what the role definition does not.
                    Invisible until 2026-09-29, which is how a custom role read
                    compensation for three months without anyone seeing it. */}
                {activeRole.disagreements?.length > 0 && (
                  <div className="bg-amber-50 border border-amber-300 rounded-lg p-4 flex items-start gap-3" data-testid="override-disagreement-banner">
                    <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-slate-900">
                        This role is enforced with more access than its own definition grants
                      </div>
                      <div className="text-xs text-slate-600 mt-0.5">
                        Saved overrides add the following. Clearing a module here removes the
                        override and the role falls back to its definition.
                      </div>
                      <ul className="mt-2 space-y-1 text-xs">
                        {activeRole.disagreements.map((d) => (
                          <li key={d.module} data-testid={`disagreement-${d.module}`}>
                            <code className="bg-white/70 px-1 rounded">{d.module}</code>
                            {' adds '}<strong>{d.adds.join(', ')}</strong>
                            {d.definition_has.length > 0
                              ? ` (definition grants ${d.definition_has.join(', ')})`
                              : ' (the definition grants nothing here)'}
                            {d.sensitive && (
                              <Badge variant="destructive" className="ml-2" data-testid={`disagreement-sensitive-${d.module}`}>pay / banking / tax</Badge>
                            )}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}

                {/* Sensitive-data role: pay, banking and tax modules are refused
                    for a custom role until an admin says why they are needed. */}
                {activeRole.is_custom && !activeRole.locked && (
                  <SensitiveDataRoleCard role={activeRole} onChanged={load} />
                )}

                {/* HR sub-area grants — hidden for custom roles per directive */}
                {!activeRole.is_custom && (
                <Card>
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <div>
                        <CardTitle className="text-base">HR sub-area grants</CardTitle>
                        <CardDescription>Role-default grants. Per-user overrides apply on top from the Users screen.</CardDescription>
                      </div>
                      <button
                        type="button"
                        onClick={() => setShowOnlyOverrides(v => !v)}
                        data-testid="toggle-overrides-filter-btn"
                        className={`text-xs px-2.5 py-1.5 rounded-md border min-h-[36px] ${
                          showOnlyOverrides ? 'bg-amber-50 text-amber-800 border-amber-200' : 'bg-surface text-slate-600 border-slate-200 hover:bg-slate-50'
                        }`}
                      >
                        <FilterX className="w-3.5 h-3.5 inline mr-1" />
                        {showOnlyOverrides ? 'Showing only modified' : 'Show only overrides'}
                      </button>
                    </div>
                  </CardHeader>
                  <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {Object.entries(HR_GRANT_META).map(([key, meta]) => {
                      const enabled = (activeRole.hr_grants || []).includes(key);
                      const defEnabled = (activeRole.default_hr_grants || []).includes(key);
                      const overridden = enabled !== defEnabled;
                      return (
                        <div
                          key={key}
                          className={`p-3 rounded-lg border ${overridden ? 'bg-amber-50 border-amber-200' : 'bg-surface border-slate-200'} ${
                            meta.highSensitivity ? 'ring-1 ring-rose-200' : ''
                          }`}
                          data-testid={`hr-grant-${key}`}
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <div className="flex items-center gap-1.5">
                                <Label className="text-sm font-semibold">{meta.label}</Label>
                                {meta.highSensitivity && (
                                  <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide bg-rose-100 text-rose-700 px-1.5 py-0.5 rounded">
                                    <Lock className="w-3 h-3" /> highest sensitivity
                                  </span>
                                )}
                              </div>
                              <div className="text-xs text-slate-600 mt-0.5">{meta.desc}</div>
                            </div>
                            <Switch
                              checked={enabled}
                              disabled={activeRole.locked}
                              onCheckedChange={(v) => toggleHRGrant(activeRole.key, key, v)}
                              data-testid={`hr-grant-toggle-${key}`}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </CardContent>
                </Card>
                )}

                {/* Modules — collapsible categories */}
                {categories.map(([category, mods]) => {
                  const open = openCategories[category];
                  const visibleMods = showOnlyOverrides
                    ? mods.filter(m => isModuleOverridden(activeRole, m.key))
                    : mods;
                  if (showOnlyOverrides && visibleMods.length === 0) return null;
                  return (
                    <Card key={category}>
                      <button
                        type="button"
                        onClick={() => setOpenCategories(p => ({ ...p, [category]: !open }))}
                        className="w-full flex items-center justify-between p-4 hover:bg-slate-50 rounded-t-lg min-h-[56px]"
                        data-testid={`category-toggle-${category}`}
                      >
                        <div className="flex items-center gap-2">
                          {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                          <span className="font-semibold text-slate-900">{category}</span>
                          <span className="text-xs text-slate-500">({visibleMods.length})</span>
                        </div>
                      </button>
                      {open && (
                        <CardContent className="space-y-3 pt-0">
                          {visibleMods.map(mod => (
                            <ModuleRow
                              key={mod.key}
                              role={activeRole}
                              mod={mod}
                              applyChip={(chip) => applyChip(activeRole.key, mod.key, chip)}
                              toggleCap={(cap, val) => toggleCap(activeRole.key, mod.key, cap, val)}
                            />
                          ))}
                        </CardContent>
                      )}
                    </Card>
                  );
                })}
              </div>
            )}
          </div>
        </TabsContent>

        {/* ─── Matrix Overview ───────────────────────── */}
        <TabsContent value="matrix">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Permission Matrix</CardTitle>
              <CardDescription>Read-only heatmap. Click any cell to jump to the Role Editor.</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto -mx-4 sm:mx-0">
                <table className="min-w-[700px] w-full text-sm">
                  <thead>
                    <tr>
                      <th className="text-left p-2 sticky left-0 bg-surface z-10 border-b border-slate-200">Module</th>
                      {snapshot.roles.map(r => (
                        <th key={r.key} className="p-2 text-xs font-semibold text-slate-700 whitespace-nowrap border-b border-slate-200">
                          {r.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {categorize(snapshot.modules).map(([category, mods]) => (
                      <React.Fragment key={category}>
                        <tr><td colSpan={snapshot.roles.length + 1} className="bg-slate-50 px-2 py-1.5 text-xs font-bold uppercase tracking-wide text-slate-600">{category}</td></tr>
                        {mods.map(mod => (
                          <tr key={mod.key} className="border-b border-slate-100">
                            <td className="p-2 sticky left-0 bg-surface z-10 whitespace-nowrap">{mod.label}</td>
                            {snapshot.roles.map(r => {
                              const lvl = resolveLevel(r.capabilities?.[mod.key] || []);
                              const meta = CHIP_LEVELS[lvl];
                              return (
                                <td key={r.key} className="p-1.5 text-center">
                                  <button
                                    type="button"
                                    onClick={() => setActiveRoleKey(r.key)}
                                    className={`min-h-[28px] px-2 py-0.5 rounded text-xs border ${meta.cls} hover:ring-2 hover:ring-blue-300`}
                                    data-testid={`matrix-cell-${r.key}-${mod.key}`}
                                  >
                                    {meta.label}
                                  </button>
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Executive Compensation grant confirmation */}
      <Dialog open={confirmExecComp.open} onOpenChange={(o) => !o && setConfirmExecComp({ open: false, role: null, nextState: false })}>
        <DialogContent data-testid="exec-comp-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-rose-600" />
              Grant Executive Compensation access
            </DialogTitle>
            <DialogDescription>
              This is the <strong>highest-sensitivity</strong> grant. It exposes executive pay records that are walled off
              even from general Payroll/HR access. Granting and revoking this writes an audited trail row.
            </DialogDescription>
          </DialogHeader>
          <div className="text-sm text-slate-700 bg-rose-50 border border-rose-200 rounded p-3">
            You're about to grant <code className="bg-surface px-1">executive_compensation</code> as the default for role <strong>{confirmExecComp.role}</strong>.
            Existing role users <em>do not</em> auto-inherit this — it only applies to newly created users of this role unless they're explicitly granted.
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmExecComp({ open: false, role: null, nextState: false })}>Cancel</Button>
            <Button onClick={confirmAndApplyExecComp} data-testid="exec-comp-confirm-btn" className="bg-rose-600 hover:bg-rose-700 text-rose-50">
              I understand — grant access
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Phase 3.5 — Create / Edit / Delete custom role dialogs */}
      <NewRoleDialog
        open={newRoleOpen}
        onClose={() => setNewRoleOpen(false)}
        roles={snapshot.roles}
        onCreated={(created) => { setNewRoleOpen(false); setActiveRoleKey(created.key); load(); }}
      />
      {activeRole?.is_custom && (
        <EditRoleDialog
          key={activeRole.key}
          open={editRoleOpen}
          onClose={() => setEditRoleOpen(false)}
          role={activeRole}
          onSaved={() => { setEditRoleOpen(false); load(); }}
        />
      )}
      {activeRole?.is_custom && (
        <DeleteRoleDialog
          key={`del-${activeRole.key}`}
          open={deleteRoleOpen}
          onClose={() => setDeleteRoleOpen(false)}
          role={activeRole}
          roles={snapshot.roles}
          onDeleted={(fallbackKey) => { setDeleteRoleOpen(false); setActiveRoleKey(fallbackKey); load(); }}
        />
      )}
    </div>
  );
}

// ─── Module row — chip + collapsible capabilities ────────────────────────
// ─── Sensitive-data role ─────────────────────────────────────────────────
// A custom role cannot hold Compensation, Banking, Federal W-4 or State
// Withholding until an admin marks it here and says why. Added 2026-09-29,
// after a role was found reading pay data that its own definition never
// granted, with no reason recorded and nobody to ask.
function SensitiveDataRoleCard({ role, onChanged }) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const marked = !!role.sensitive_data_role;

  const submit = async (enabled) => {
    if (reason.trim().length < 10) {
      toast.error('Give a reason of at least 10 characters — it is what makes this reviewable later');
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.put(`/custom-roles/${role.key}/sensitive-data`, {
        enabled, reason: reason.trim(),
      });
      toast.success(data.message);
      setReason('');
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not change this');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className={marked ? 'border-amber-300' : ''} data-testid="sensitive-data-role-card">
      <CardHeader className="pb-3">
        <div className="flex items-start gap-2">
          <ShieldAlert className={`w-5 h-5 shrink-0 mt-0.5 ${marked ? 'text-amber-600' : 'text-slate-400'}`} />
          <div>
            <CardTitle className="text-base">Pay, banking and tax data</CardTitle>
            <CardDescription>
              {marked
                ? 'This role may be granted Compensation, Banking, Federal W-4 and State Withholding. It is listed on System Status under "Who can read pay, banking and tax data".'
                : 'Those four modules are refused for this role. Mark it below, with a reason, if it genuinely needs them.'}
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {marked && (
          <div className="rounded-md bg-amber-50 border border-amber-200 p-3 text-xs text-slate-700" data-testid="sensitive-data-reason">
            <div><strong>Reason on record:</strong> {role.sensitive_data_reason}</div>
            {role.sensitive_data_marked_at && (
              <div className="text-slate-500 mt-0.5">Marked {new Date(role.sensitive_data_marked_at).toLocaleString()}</div>
            )}
          </div>
        )}
        <Textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={2}
          placeholder={marked
            ? 'Why is this access no longer needed?'
            : 'Why does this role need pay, banking or tax access?'}
          data-testid="sensitive-data-reason-input"
        />
        <Button
          variant={marked ? 'outline' : 'default'}
          disabled={busy}
          onClick={() => submit(!marked)}
          className={marked ? 'text-rose-700 border-rose-200 hover:bg-rose-50' : ''}
          data-testid="sensitive-data-toggle-btn"
        >
          <ShieldAlert className="w-3.5 h-3.5 mr-1" />
          {busy ? 'Saving…' : marked ? 'Revoke and strip these modules' : 'Allow pay, banking and tax modules'}
        </Button>
        {marked && (
          <p className="text-xs text-slate-500">
            Revoking also clears those four modules from this role's capabilities and from its
            saved overrides, so the change is real and not just a label.
          </p>
        )}
      </CardContent>
    </Card>
  );
}


function ModuleRow({ role, mod, applyChip, toggleCap }) {
  const [expanded, setExpanded] = useState(false);
  const caps = role.capabilities?.[mod.key] || [];
  const level = resolveLevel(caps);
  const overridden = isModuleOverridden(role, mod.key);
  const chipMeta = CHIP_LEVELS[level];
  const isSupervisor = role.key === 'supervisor';

  return (
    <div
      className={`rounded-lg border p-3 ${overridden ? 'bg-amber-50 border-amber-200' : 'bg-surface border-slate-200'}`}
      data-testid={`module-row-${role.key}-${mod.key}`}
    >
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <button
          type="button"
          onClick={() => setExpanded(e => !e)}
          className="flex items-center gap-2 text-left min-h-[36px]"
          data-testid={`expand-${mod.key}`}
        >
          {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          <span className="font-medium text-sm">{mod.label}</span>
          {overridden && <span className="text-[10px] uppercase bg-amber-200 text-amber-900 px-1.5 py-0.5 rounded">override</span>}
        </button>
        <div className="flex items-center gap-1">
          {['none', 'view', 'full', 'custom'].map(chip => (
            <button
              key={chip}
              type="button"
              disabled={chip === 'custom' || role.locked}
              onClick={() => applyChip(chip)}
              data-testid={`chip-${role.key}-${mod.key}-${chip}`}
              className={`text-xs px-2.5 py-1.5 rounded border min-h-[32px] ${
                level === chip ? `${CHIP_LEVELS[chip].cls} ring-2 ring-blue-400` : `${CHIP_LEVELS[chip].cls} opacity-70 hover:opacity-100`
              } ${chip === 'custom' ? 'cursor-default' : ''}`}
            >
              {CHIP_LEVELS[chip].label}
            </button>
          ))}
        </div>
      </div>
      {expanded && (
        <div className="mt-3 pt-3 border-t border-slate-200 grid grid-cols-2 sm:grid-cols-3 gap-2">
          {ALL_CAPS.map(cap => {
            const disabled = role.locked || (cap === 'approve' && isSupervisor);
            return (
              <label
                key={cap}
                className={`flex items-center gap-2 text-sm rounded px-2 py-1.5 ${disabled ? 'opacity-50' : 'hover:bg-slate-50'} ${caps.includes(cap) ? 'bg-blue-50' : ''}`}
              >
                <input
                  type="checkbox"
                  checked={caps.includes(cap)}
                  disabled={disabled}
                  onChange={(e) => toggleCap(cap, e.target.checked)}
                  data-testid={`cap-${role.key}-${mod.key}-${cap}`}
                  className="w-4 h-4"
                />
                <span>{CAP_LABEL[cap]}</span>
                {cap === 'approve' && isSupervisor && <span className="text-[10px] text-slate-500">(N/A)</span>}
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
}


// ─── Custom-role dialogs (Phase 3.5) ─────────────────────────────────────
const SCOPES = [
  { value: 'all', label: 'All records' },
  { value: 'region', label: 'Region (state-scoped)' },
  { value: 'location', label: 'Single location' },
  { value: 'team', label: 'Direct reports (team)' },
  { value: 'self', label: 'Self only' },
];

function NewRoleDialog({ open, onClose, roles, onCreated }) {
  const [label, setLabel] = useState('');
  const [mode, setMode] = useState('clone');                 // 'clone' | 'blank'
  const [baseRole, setBaseRole] = useState('manager');
  const [scope, setScope] = useState('self');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setLabel(''); setMode('clone'); setBaseRole('manager'); setScope('self'); setDescription('');
    }
  }, [open]);

  const cloneOptions = roles.filter(r => !r.is_custom);       // only built-ins as templates

  const submit = async () => {
    if (!label.trim() || label.trim().length < 3) {
      toast.error('Label must be at least 3 characters');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        label: label.trim(),
        base_role: mode === 'clone' ? baseRole : null,
        default_scope: scope,
        description: description.trim() || null,
      };
      const res = await api.post('/custom-roles', payload);
      toast.success(`Created role "${res.data.label}"`);
      onCreated(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create role');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="new-role-dialog" className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-blue-600" /> New custom role
          </DialogTitle>
          <DialogDescription>
            Create an admin-defined role with its own capability map. HR sub-area grants are not
            allowed on custom roles — grant those per-user from the Users screen.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="mb-1.5 block text-sm">Role label</Label>
            <Input value={label} onChange={(e) => setLabel(e.target.value)}
                   placeholder="e.g. Regional Security Director"
                   data-testid="new-role-label" maxLength={60} />
            <div className="text-[11px] text-slate-500 mt-1">
              A machine key will be auto-generated from this label.
            </div>
          </div>

          <div>
            <Label className="mb-1.5 block text-sm">Starting capabilities</Label>
            <RadioGroup value={mode} onValueChange={setMode} className="space-y-2">
              <label className="flex items-start gap-2 cursor-pointer">
                <RadioGroupItem value="clone" data-testid="new-role-mode-clone" />
                <div className="text-sm">
                  <span className="font-medium">Clone from existing role</span>
                  <div className="text-xs text-slate-500">Start with another role's capability map and tweak from there.</div>
                </div>
              </label>
              {mode === 'clone' && (
                <div className="pl-6">
                  <Select value={baseRole} onValueChange={setBaseRole}>
                    <SelectTrigger data-testid="new-role-base"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {cloneOptions.map(r => (
                        <SelectItem key={r.key} value={r.key}>{r.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <label className="flex items-start gap-2 cursor-pointer">
                <RadioGroupItem value="blank" data-testid="new-role-mode-blank" />
                <div className="text-sm">
                  <span className="font-medium">Start blank</span>
                  <div className="text-xs text-slate-500">Every module starts at No Access — build up from zero.</div>
                </div>
              </label>
            </RadioGroup>
          </div>

          <div>
            <Label className="mb-1.5 block text-sm">Default record scope</Label>
            <Select value={scope} onValueChange={setScope}>
              <SelectTrigger data-testid="new-role-scope"><SelectValue /></SelectTrigger>
              <SelectContent>
                {SCOPES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <div className="text-[11px] text-slate-500 mt-1">
              Individual users assigned this role can have their scope tightened further.
            </div>
          </div>

          <div>
            <Label className="mb-1.5 block text-sm">Description (optional)</Label>
            <Textarea value={description} onChange={(e) => setDescription(e.target.value)}
                      placeholder="Who this role is for / when to use it" rows={2}
                      data-testid="new-role-description" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={submit} disabled={saving || !label.trim()} data-testid="new-role-submit">
            {saving ? 'Creating…' : 'Create role'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditRoleDialog({ open, onClose, role, onSaved }) {
  const [label, setLabel] = useState(role.label);
  const [scope, setScope] = useState(role.default_scope);
  const [description, setDescription] = useState(role.description || '');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setLabel(role.label); setScope(role.default_scope); setDescription(role.description || '');
    }
  }, [open, role]);

  const submit = async () => {
    setSaving(true);
    try {
      await api.patch(`/custom-roles/${role.key}`, {
        label: label.trim(), default_scope: scope, description: description.trim() || null,
      });
      toast.success('Role updated');
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="edit-role-dialog" className="max-w-md">
        <DialogHeader>
          <DialogTitle>Edit role · {role.label}</DialogTitle>
          <DialogDescription>Update metadata. Capability changes are saved via the main Save button.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="mb-1.5 block text-sm">Role label</Label>
            <Input value={label} onChange={(e) => setLabel(e.target.value)} maxLength={60} data-testid="edit-role-label" />
          </div>
          <div>
            <Label className="mb-1.5 block text-sm">Default record scope</Label>
            <Select value={scope} onValueChange={setScope}>
              <SelectTrigger data-testid="edit-role-scope"><SelectValue /></SelectTrigger>
              <SelectContent>
                {SCOPES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="mb-1.5 block text-sm">Description</Label>
            <Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} data-testid="edit-role-description" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="edit-role-submit">
            {saving ? 'Saving…' : 'Save changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DeleteRoleDialog({ open, onClose, role, roles, onDeleted }) {
  const [usage, setUsage] = useState(null);                    // {primary, addon, total}
  const [fallbackRole, setFallbackRole] = useState('manager');
  const [busy, setBusy] = useState(false);

  // Pull live usage count when the dialog opens.
  useEffect(() => {
    let ignore = false;
    if (open) {
      setUsage(null);
      api.get(`/custom-roles/${role.key}/usage`)
        .then(res => { if (!ignore) setUsage(res.data); })
        .catch(() => { if (!ignore) setUsage({ primary: 0, addon: 0, total: 0 }); });
    }
    return () => { ignore = true; };
  }, [open, role.key]);

  const fallbackOptions = roles.filter(r => r.key !== role.key && !r.locked);
  // Default to first available built-in if current selection is the role being deleted.
  useEffect(() => { if (fallbackRole === role.key) setFallbackRole('manager'); }, [role.key, fallbackRole]);

  const submit = async () => {
    setBusy(true);
    try {
      await api.delete(`/custom-roles/${role.key}`, { data: { fallback_role: fallbackRole } });
      toast.success(`Deleted "${role.label}" · users migrated to ${fallbackRole}`);
      onDeleted(fallbackRole);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="delete-role-dialog" className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Trash2 className="w-5 h-5 text-rose-600" /> Delete role · {role.label}
          </DialogTitle>
          <DialogDescription>
            This deletes the custom role and migrates affected users to a fallback role. The action is audited.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="bg-amber-50 border border-amber-200 rounded p-3 text-sm">
            {usage === null && <span className="text-slate-500">Checking usage…</span>}
            {usage && usage.total === 0 && <span>No users currently hold this role — clean delete.</span>}
            {usage && usage.total > 0 && (
              <span>
                <strong>{usage.primary}</strong> user(s) hold this as their primary role.
                {usage.addon > 0 && <> {usage.addon} have it as an add-on (will be removed).</>}
              </span>
            )}
          </div>
          <div>
            <Label className="mb-1.5 block text-sm">Migrate users to fallback role</Label>
            <Select value={fallbackRole} onValueChange={setFallbackRole}>
              <SelectTrigger data-testid="delete-role-fallback"><SelectValue /></SelectTrigger>
              <SelectContent>
                {fallbackOptions.map(r => (
                  <SelectItem key={r.key} value={r.key}>{r.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="text-[11px] text-slate-500 mt-1">
              Any user whose primary role is "{role.label}" will be re-assigned to this fallback.
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={submit} disabled={busy || !fallbackRole}
                  className="bg-rose-600 hover:bg-rose-700 text-rose-50" data-testid="delete-role-submit">
            {busy ? 'Deleting…' : 'Delete & migrate'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
